import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveAppNavIcon } from '@/lib/app-nav-icon';
import { badgeToneClass } from './nav-types';
import type { NavLink } from './nav-types';

/**
 * Dropdown + mobile-group state shared by every place a header renders the
 * nav (classic inline row, desktop row, mobile drawer). Single open dropdown
 * at a time — matches the user-menu pattern. We can't use a single ref to
 * detect click-outside because the same nav renders in up to three places;
 * instead, dropdown triggers and panels carry a `data-nav-dropdown` attribute
 * so the outside-click handler can recognize any of them.
 */
export function useNavDropdowns(resetKey: unknown) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileGroups, setOpenMobileGroups] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!openDropdown) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenDropdown(null);
    }
    function onClick(e: MouseEvent) {
      const target = e.target as Element | null;
      if (target?.closest('[data-nav-dropdown]')) return;
      setOpenDropdown(null);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [openDropdown]);

  // Close any open dropdown / mobile group whenever the nav surface changes
  // (e.g. entering or leaving a replace-mode app mount).
  useEffect(() => {
    setOpenDropdown(null);
    setOpenMobileGroups(new Set());
  }, [resetKey]);

  function toggleMobileGroup(label: string) {
    setOpenMobileGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  return { openDropdown, setOpenDropdown, openMobileGroups, toggleMobileGroup };
}

export type NavDropdowns = ReturnType<typeof useNavDropdowns>;

interface DesktopNavLinkProps {
  link: NavLink;
  linkClass: string;
  dropdowns: NavDropdowns;
}

/**
 * One desktop nav item: an Axiss opener, a dropdown parent (v1.6: button
 * trigger with ChevronDown; clicking opens a panel of children anchored
 * below), or a plain link with an optional icon and badge.
 */
export function DesktopNavLink({ link, linkClass, dropdowns }: DesktopNavLinkProps) {
  const { openDropdown, setOpenDropdown } = dropdowns;
  if (link.axissOpen) {
    return (
      <button
        data-axiss-open={link.axissView || 'home'}
        className={cn(linkClass, 'cursor-pointer')}
      >
        {link.label}
      </button>
    );
  }
  if (link.children && link.children.length > 0) {
    const isOpen = openDropdown === link.label;
    return (
      <div className="relative" data-nav-dropdown>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          onClick={() => setOpenDropdown(isOpen ? null : link.label)}
          className={cn(linkClass, 'inline-flex items-center gap-1 cursor-pointer')}
        >
          {link.label}
          {link.badge && (
            <span className={cn('ml-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(link.badge.tone))}>
              {link.badge.text}
            </span>
          )}
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        </button>
        {isOpen && (
          <div
            role="menu"
            data-nav-dropdown
            className="absolute left-0 top-full mt-1 min-w-[12rem] bg-surface rounded-lg shadow-lg border border-sage-200 z-30 py-1"
          >
            {link.children.map((child) =>
              child.href ? (
                <Link
                  key={child.href}
                  to={child.href}
                  role="menuitem"
                  onClick={() => setOpenDropdown(null)}
                  className="flex items-center justify-between gap-3 px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none"
                >
                  <span>{child.label}</span>
                  {child.badge && (
                    <span className={cn('text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(child.badge.tone))}>
                      {child.badge.text}
                    </span>
                  )}
                </Link>
              ) : null,
            )}
          </div>
        )}
      </div>
    );
  }
  const Icon = resolveAppNavIcon(link.icon);
  return (
    <Link
      to={link.href!}
      className={cn(linkClass, 'inline-flex items-center gap-1.5')}
    >
      {Icon && <Icon className="h-4 w-4" aria-hidden />}
      {link.label}
      {link.badge && (
        <span className={cn('text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(link.badge.tone))}>
          {link.badge.text}
        </span>
      )}
    </Link>
  );
}

interface MobileNavLinkProps {
  link: NavLink;
  dropdowns: NavDropdowns;
  closeMobileMenu: () => void;
  inReplaceMode: boolean;
  /** Base class for the row; the drawer and the sheet differ in colour. */
  baseClass?: string;
}

const DRAWER_LINK_CLASS =
  'block w-full text-left px-3 py-3 text-base font-medium text-sage-800 hover:bg-sage-50 rounded-lg min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';

/** One mobile menu item: Axiss opener, expandable group, or plain link. */
export function MobileNavLink({
  link,
  dropdowns,
  closeMobileMenu,
  inReplaceMode,
  baseClass = DRAWER_LINK_CLASS,
}: MobileNavLinkProps) {
  const { openMobileGroups, toggleMobileGroup } = dropdowns;
  if (link.axissOpen) {
    return (
      <button
        data-axiss-open={link.axissView || 'home'}
        className={cn(baseClass, 'cursor-pointer flex items-center')}
        onClick={closeMobileMenu}
      >
        {link.label}
      </button>
    );
  }
  if (link.children && link.children.length > 0) {
    const isOpen = openMobileGroups.has(link.label);
    return (
      <div>
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => toggleMobileGroup(link.label)}
          className={cn(baseClass, 'flex items-center justify-between')}
        >
          <span className="flex items-center gap-2">
            {link.label}
            {link.badge && (
              <span className={cn('text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(link.badge.tone))}>
                {link.badge.text}
              </span>
            )}
          </span>
          <ChevronRight className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-90')} aria-hidden="true" />
        </button>
        {isOpen && (
          <div className="pl-4 border-l border-sage-100 ml-3 mt-1 space-y-1">
            {link.children.map((child) =>
              child.href ? (
                <Link
                  key={child.href}
                  to={child.href}
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                >
                  {child.label}
                </Link>
              ) : null,
            )}
          </div>
        )}
      </div>
    );
  }
  const Icon = resolveAppNavIcon(link.icon);
  return (
    <Link
      to={link.href!}
      className={cn(baseClass, 'flex items-center gap-2')}
      onClick={closeMobileMenu}
    >
      {link.href === '/' && inReplaceMode && <ArrowLeft className="h-4 w-4" aria-hidden="true" />}
      {Icon && <Icon className="h-4 w-4" aria-hidden />}
      {link.label}
      {link.badge && (
        <span className={cn('text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(link.badge.tone))}>
          {link.badge.text}
        </span>
      )}
    </Link>
  );
}

/** Stable React key for a nav link in a list. */
export function navLinkKey(link: NavLink): string {
  return link.href ?? link.label;
}
